type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private readonly MAX_LOGS = 1000;

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatMessage(level: LogLevel, message: string, data?: any): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data: data ? this.sanitizeData(data) : undefined
    };
  }

  private sanitizeData(data: any): any {
    if (typeof data !== 'object') return data;
    
    const sanitized = { ...data };
    // Redact sensitive information
    if (sanitized.message) sanitized.message = '[REDACTED]';
    if (sanitized.password) sanitized.password = '[REDACTED]';
    return sanitized;
  }

  private addLog(entry: LogEntry) {
    this.logs.push(entry);
    if (this.logs.length > this.MAX_LOGS) {
      this.logs.shift();
    }
  }

  info(message: string, data?: any) {
    const entry = this.formatMessage('info', message, data);
    this.addLog(entry);
  }

  warn(message: string, data?: any) {
    const entry = this.formatMessage('warn', message, data);
    this.addLog(entry);
  }

  error(message: string, data?: any) {
    const entry = this.formatMessage('error', message, data);
    this.addLog(entry);
  }

  debug(message: string, data?: any) {
    if (process.env.NODE_ENV === 'development') {
      const entry = this.formatMessage('debug', message, data);
      this.addLog(entry);
    }
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }
}

export const logger = Logger.getInstance(); 